import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getEdges } from "../api/edgeApi";
import {
  getAlarmRules,
  deleteAlarmRule,
  toggleAlarmRule,
  CONDITION_TYPES,
  SENSOR_FIELDS,
} from "../api/alarmRuleApi";
import AlarmRuleForm from "../components/AlarmRules/AlarmRuleForm";

export default function AlarmList() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);

  const [rules, setRules] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);

  // Edge listesini yükle
  useEffect(() => {
    if (!accessToken) return;

    async function fetchEdges() {
      try {
        const data = await getEdges(accessToken);
        // Lite-tier edge'ler (cloud_transfer_enabled=False) hiç telemetri
        // göndermiyor -- alarm kuralı yazılabilecek bir sensör verisi yok,
        // seçilebilir olması kullanıcıyı boş bir kural oluşturmaya götürür.
        setEdges((Array.isArray(data) ? data : []).filter((e) => e.cloud_transfer_enabled));
      } catch (err) {
        console.error("Could not load the edge list:", err);
      }
    }

    fetchEdges();
  }, [accessToken]);

  // Alarm kurallarını yükle
  const fetchRules = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    try {
      const data = await getAlarmRules(accessToken);
      setRules(data);
    } catch (err) {
      console.error("Could not load the alarm rules:", err);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const getConditionLabel = (condition) => {
    const ct = CONDITION_TYPES.find((c) => c.value === condition);
    return ct ? ct.symbol : condition;
  };

  const getFieldLabel = (field) => {
    const sf = SENSOR_FIELDS.find((s) => s.value === field);
    return sf ? `${sf.label} (${sf.unit})` : field;
  };

  const getEdgeName = (edgeId) => {
    const edge = edges.find((e) => e.id === edgeId);
    return edge ? (edge.name ?? edge.edgeId) : edgeId;
  };

  const handleEditRule = (rule) => {
    setEditRule(rule);
    setModalOpen(true);
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm(t("alarms.common.confirmDelete"))) return;

    try {
      await deleteAlarmRule(accessToken, ruleId);
      fetchRules();
    } catch (err) {
      console.error("Could not delete the rule:", err);
      toast.error(t("alarms.list.deleteFailed"));
    }
  };

  const handleToggleRule = async (ruleId) => {
    try {
      await toggleAlarmRule(accessToken, ruleId);
      fetchRules();
    } catch (err) {
      console.error("Could not change the rule status:", err);
      toast.error(t("alarms.list.toggleFailed"));
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditRule(null);
    fetchRules();
  };

  const parseActions = (actionsStr) => {
    if (!actionsStr) return [];
    try {
      return JSON.parse(actionsStr);
    } catch {
      return [];
    }
  };

  // eslint-disable-next-line no-unused-vars -- ileride aksiyon formatı için
  const formatActions = (actions) => {
    if (!actions || actions.length === 0) return "-";
    return actions.map((a) => a.type).join(", ");
  };

  if (loading && rules.length === 0) {
    return <div style={styles.container}>{t("alarms.common.loading")}</div>;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>{t("alarms.list.title")}</h1>
        <div style={styles.headerActions}>
          <button
            style={styles.addRuleBtn}
            onClick={() => {
              setEditRule(null);
              setModalOpen(true);
            }}
          >
            {t("alarms.list.addRule")}
          </button>
        </div>
      </div>

      {/* Alarm Kuralları */}
        <div style={styles.tableWrapper}>
          {rules.length === 0 ? (
            <div style={styles.emptyState}>
              <p>{t("alarms.common.empty")}</p>
              <button
                style={styles.addRuleBtn}
                onClick={() => {
                  setEditRule(null);
                  setModalOpen(true);
                }}
              >
                {t("alarms.list.firstRule")}
              </button>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t("alarms.list.col.status")}</th>
                  <th style={styles.th}>{t("alarms.list.col.name")}</th>
                  <th style={styles.th}>{t("alarms.list.col.edge")}</th>
                  <th style={styles.th}>{t("alarms.list.col.condition")}</th>
                  <th style={styles.th}>{t("alarms.list.col.actions")}</th>
                  <th style={styles.th}>{t("alarms.list.col.check")}</th>
                  <th style={styles.th}>{t("alarms.list.col.ops")}</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id} style={styles.tr}>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          backgroundColor: rule.is_active ? "var(--success)" : "#6c757d",
                        }}
                      >
                        {rule.is_active ? t("alarms.list.active") : t("alarms.list.inactive")}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.ruleName}>{rule.name}</div>
                    </td>
                    <td style={styles.td}>{getEdgeName(rule.edge_id)}</td>
                    <td style={styles.td}>
                      <code style={styles.condition}>
                        {getFieldLabel(rule.field)} {getConditionLabel(rule.condition)}{" "}
                        {rule.threshold}
                        {rule.condition === "BETWEEN" && ` - ${rule.threshold2}`}
                      </code>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionBadges}>
                        {parseActions(rule.actions).map((action, idx) => (
                          <span key={idx} style={styles.actionBadge}>
                            {action.type}
                          </span>
                        ))}
                        {parseActions(rule.actions).length === 0 && "-"}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.intervalBadge}>
                        {t("alarms.list.interval", { seconds: rule.check_interval_seconds, minutes: rule.cooldown_minutes })}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          style={styles.actionBtn}
                          onClick={() => handleToggleRule(rule.id)}
                          title={rule.is_active ? t("alarms.list.disableTitle") : t("alarms.list.enableTitle")}
                        >
                          {rule.is_active ? t("alarms.list.stop") : t("alarms.list.start")}
                        </button>
                        <button
                          style={styles.actionBtn}
                          onClick={() => handleEditRule(rule)}
                        >
                          {t("alarms.common.edit")}
                        </button>
                        <button
                          style={{ ...styles.actionBtn, color: "var(--danger)" }}
                          onClick={() => handleDeleteRule(rule.id)}
                        >
                          {t("alarms.common.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      {/* Alarm Rule Form Modal */}
      {modalOpen && (
        <AlarmRuleForm
          edgeDbId={edges[0]?.id || ""}
          edges={edges}
          rule={editRule}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    color: "var(--text-primary)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "bold",
    margin: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  addRuleBtn: {
    backgroundColor: "var(--btn-primary)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "10px 20px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: "500",
  },
  tabs: {
    display: "flex",
    gap: "4px",
    marginBottom: "20px",
    borderBottom: "1px solid var(--border)",
  },
  tab: {
    backgroundColor: "transparent",
    color: "var(--text-muted)",
    border: "none",
    borderBottom: "2px solid transparent",
    padding: "12px 20px",
    fontSize: "14px",
    cursor: "pointer",
    marginBottom: "-1px",
  },
  tabActive: {
    backgroundColor: "transparent",
    color: "var(--text-primary)",
    border: "none",
    borderBottom: "2px solid var(--btn-primary)",
    padding: "12px 20px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: "500",
    marginBottom: "-1px",
  },
  filters: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
  },
  filterBtn: {
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-primary)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    padding: "8px 16px",
    fontSize: "13px",
    cursor: "pointer",
  },
  filterActive: {
    backgroundColor: "var(--btn-primary)",
    color: "#fff",
    border: "1px solid var(--btn-primary)",
    borderRadius: "6px",
    padding: "8px 16px",
    fontSize: "13px",
    cursor: "pointer",
  },
  tableWrapper: {
    backgroundColor: "var(--bg-surface)",
    borderRadius: "12px",
    border: "1px solid var(--border)",
    overflow: "hidden",
  },
  emptyState: {
    padding: "48px 24px",
    textAlign: "center",
    color: "var(--text-muted)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "14px 16px",
    color: "var(--text-muted)",
    fontWeight: "500",
    fontSize: "13px",
    borderBottom: "1px solid var(--border)",
    backgroundColor: "var(--bg-surface)",
  },
  tr: {
    borderBottom: "1px solid var(--border)",
  },
  td: {
    padding: "14px 16px",
    fontSize: "14px",
  },
  ruleName: {
    fontWeight: "500",
    color: "var(--text-primary)",
  },
  statusBadge: {
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "500",
    color: "#fff",
  },
  condition: {
    backgroundColor: "var(--bg-hover)",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "13px",
    color: "#ff9800",
  },
  actionBadges: {
    display: "flex",
    gap: "4px",
    flexWrap: "wrap",
  },
  actionBadge: {
    backgroundColor: "rgba(15, 39, 68, 0.2)",
    color: "var(--accent-navy-light)",
    padding: "4px 8px",
    borderRadius: "8px",
    fontSize: "11px",
    fontWeight: "500",
  },
  intervalBadge: {
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-muted)",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
  },
  actions: {
    display: "flex",
    gap: "8px",
  },
  actionBtn: {
    background: "none",
    border: "none",
    color: "var(--accent-navy-light)",
    fontSize: "13px",
    cursor: "pointer",
    padding: "4px",
  },
};
